import React, {FC, memo, useCallback, useMemo, useState, useEffect} from "react";
import {NavigationProps} from "../../utils/navigationProps";
import {CardMarkupViewer} from "../../markup/cardMarkupViewer/cardMarkupViewer";
import {useDispatch, useSelector} from "react-redux";
import {usePageTitle} from "../../hooks/usePageTitle";
import {cardQuestionText} from "../../utils/cardQuestionText";
import {RootState} from "../../store/rootReducer";
import {CardMarkupViewerState} from "../../markup/cardMarkupViewer/cardMarkupViewer.interface";
import {CardDownloader} from "../downloader/cardDownloader";
import {FloatingButton} from "../editor/floatingButton/floatingButton";
import {FloatingButtonIcon} from "../editor/floatingButton/floatingButtons.interface";
import {useNavigate} from "@reach/router";
import {formatInterval} from "../../utils/formatInterval";
import {CardActionBar} from "./actionBar/cardActionBar";
import {api} from "../../api/api";
import {cardsActions} from "../cards.slice";
import {CardState, markupFormatter} from "../../common";
import styled from "styled-components";
import {ThemeProps} from "../../styles/theme";
import {NotFound404} from "../../components/NotFound404";
import {subcardQuestionLine} from "../mark/markInstructions";

function formatDate(time: number) {
    const date = new Date(time);
    return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
}

function formatDateTime(time: number) {
    const date = new Date(time);
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return formatDate(time) + ' ' + date.getHours() + ':' + minutes;
}

type Props = NavigationProps & {
    cardId?: string;
}

interface CardProps extends ThemeProps {
    $isSmall?: boolean;
}

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    position: fixed; /* Restore fixed positioning */
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;

const CardContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start; /* Start from the top for long cards */
    padding: 20px;
    padding-top: 30px; /* Increased top padding to create space between action bar and card */
    overflow-y: auto;
    height: calc(100% - 48px); /* Adjust for action bar height */
    box-sizing: border-box;
    min-height: 0; /* Ensure flex items can be smaller than content */
`;

const Card = styled.div<CardProps>`
    width: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    background-color: ${props => props.theme.primaryBackground};
    padding: 20px;
    box-sizing: border-box;
    border: ${props => props.theme.primaryBorder} 2px solid;
    box-shadow: 4px 4px ${props => props.theme.cardShadow};
    /* Ensure images don't overflow */
    img {
        max-width: 100%;
        height: auto;
    }
    margin: ${(props: CardProps) => props.$isSmall === true ? 'auto 0' : '0'}; /* Only center small cards */
    
`;

const ResetButton = styled.button<ThemeProps>`
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 0.8em;
    border: ${props => props.theme.primaryBorder} 1px solid;
    border-radius: 4px;
    background-color: transparent;
    color: ${props => props.theme.primaryForeground};
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.7;
    }
`;

const GenerationNotice = styled.div<ThemeProps>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 12px;
    font-size: 0.9em;
    color: ${props => props.theme.primaryForeground};
`;

const MarkNote = styled.span`
    white-space: pre-wrap;
`;

const SubcardMarkList = styled.ul`
    width: 100%;
    margin: 4px 0 0;
    padding-left: 18px;
`;

const SubcardMarkItem = styled.li`
    margin-bottom: 4px;

    & > span {
        overflow-wrap: anywhere;
    }
`;

const GENERATION_LABELS = {
    regenerate: {running: 'Regenerating in the background — you can leave this page', failed: 'Regeneration failed'},
    invert: {running: 'Inverting in the background — you can leave this page', failed: 'Inversion failed'},
};

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 10px;
`;

const Tag = styled.div<ThemeProps>`
    background-color: ${props => props.theme.accentColor};
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const MetaPanel = styled.div`
    width: 100%;
    max-width: 800px;
    box-sizing: border-box;
    margin-bottom: 15px;
`;

const MetaToggle = styled.button<ThemeProps>`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 8px 12px;
    background-color: transparent;
    border: ${props => props.theme.primaryBorder} 2px solid;
    color: ${props => props.theme.primaryForeground};
    cursor: pointer;
    font-size: 0.9em;
    text-align: left;

    &:hover {
        opacity: 0.8;
    }
`;

const Caret = styled.span<{ $open: boolean }>`
    display: inline-block;
    transition: transform 0.15s ease;
    transform: rotate(${props => props.$open === true ? '90deg' : '0deg'});
`;

const MetaBody = styled.div<ThemeProps>`
    display: flex;
    flex-direction: column;
    padding: 12px;
    border: ${props => props.theme.primaryBorder} 2px solid;
    border-top: none;
`;


const CardViewComponent: FC<Props> = (props) => {
    const selector = useCallback((state: RootState) => ({
        card: props.cardId !== undefined
            ? state.cards[props.cardId]
            : undefined,
    }), [props.cardId]);

    const {card} = useSelector(selector);

    const pageTitle = useMemo(() => {
        const q = card ? cardQuestionText(card.markup) : '';
        return `${q || 'Card'} · Memkeep`;
    }, [card]);
    usePageTitle(pageTitle);

    const [loading, setLoading] = useState<boolean>(true);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
    
    // Simple effect to handle loading and not found states
    useEffect(() => {
        if (props.cardId === undefined || props.cardId === null || props.cardId === '') {
            // No card ID provided
            setLoading(false);
            setNotFound(true);
            return;
        }
        
        // Card ID has changed - start loading
        setLoading(true);
        setNotFound(false);
        
        // Use a short timeout only for detecting 404, not for normal loading
        const notFoundTimer: NodeJS.Timeout = setTimeout(() => {
            // If still undefined after 300ms, mark as not found
            if (card === undefined) {
                setNotFound(true);
                setLoading(false);
            }
        }, 300);
        
        // If card loads quickly, immediately stop loading
        if (card !== undefined) {
            setLoading(false);
            setNotFound(false);
            clearTimeout(notFoundTimer);
            return;
        }
        
        return () => {
            if (notFoundTimer !== undefined) {
                clearTimeout(notFoundTimer);
            }
        };
    }, [props.cardId, card]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const editHandler = useCallback(() => {
        navigate(`/edit/${props.cardId}`);
    }, [navigate, props.cardId]);

    const resetNextCheckHandler = useCallback(() => {
        const cardId = props.cardId;
        if (cardId === undefined || cardId === '') return;
        void api.resetNextCheck({cardId}).then(() => {
            dispatch(cardsActions.loadCard(cardId));
        });
    }, [dispatch, props.cardId]);

    const unmarkWholeCardHandler = useCallback(() => {
        const cardId = props.cardId;
        if (cardId === undefined || cardId === '') return;
        dispatch(cardsActions.unmarkWholeCard(cardId));
    }, [dispatch, props.cardId]);

    const unmarkSubcardHandler = useCallback((subcardKey: string) => {
        const cardId = props.cardId;
        if (cardId === undefined || cardId === '') return;
        dispatch(cardsActions.unmarkSubcard({cardId, subcardKey}));
    }, [dispatch, props.cardId]);

    const dismissGenerationHandler = useCallback(() => {
        const cardId = props.cardId;
        if (cardId === undefined || cardId === '') return;
        void api.dismissCardGeneration(cardId).then(() => {
            dispatch(cardsActions.setGeneration({cardId, generation: undefined}));
        });
    }, [dispatch, props.cardId]);

    const tags = useMemo(() => {
        if (!card) return [];
        return markupFormatter.parse(card.markup).tags;
    }, [card]);

    const hasSubCards = useMemo(() => {
        if (!card) return false;
        return markupFormatter.parse(card.markup).hasSubCards;
    }, [card]);

    // Per-subcard accuracy stats, shown as badges on each subcard.
    const [subcardStats, setSubcardStats] = useState<Record<string, { correct: number; total: number }>>({});
    useEffect(() => {
        if (!hasSubCards || props.cardId === undefined || props.cardId === '') {
            setSubcardStats({});
            return;
        }
        let cancelled = false;
        api.statistics.getSubcardHeatmap(props.cardId)
            .then(data => {
                if (!cancelled) setSubcardStats(data.stats);
            })
            .catch(() => {
                if (!cancelled) setSubcardStats({});
            });
        return () => {
            cancelled = true;
        };
    }, [props.cardId, hasSubCards]);

    const handleTagClick = useCallback((tag: string) => {
        navigate(`/search?tag=${encodeURIComponent(tag)}`);
    }, [navigate]);


    // Determine if card content is small enough to be centered
    const isSmallCard = useMemo(() => {
        if (card === undefined) return true;
        // Simple heuristic - cards with less than 300 characters are considered small
        return card.markup.length < 300;
    }, [card]);
    
    // Show 404 page if card ID is provided but no card was found after loading completed
    if (loading === false && notFound === true) {
        return <NotFound404 type="card" id={props.cardId} />;
    }
    
    // Show loading message
    if (loading === true) {
        return (
            <Root>
                {/* Always include CardDownloader even during loading */}
                <CardDownloader cardId={props.cardId}/>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    Loading card...
                </div>
            </Root>
        );
    }

    return (
        <Root>
            {/* CardDownloader must always be rendered regardless of card existence */}
            <CardDownloader cardId={props.cardId}/>
            {card !== undefined && (
                <>
                    <CardActionBar
                        cardId={props.cardId !== undefined ? props.cardId : ''}
                    />
                    <CardContainer>
                        <MetaPanel>
                            <MetaToggle onClick={() => setDetailsOpen(open => !open)}>
                                <Caret $open={detailsOpen}>▸</Caret>
                                Details
                            </MetaToggle>
                            {detailsOpen && (
                                <MetaBody>
                                    <span>State: {CardState[card.state]}</span>
                                    {card.state === CardState.Known && (
                                        <span>Interval: {formatInterval(card.interval)}</span>
                                    )}
                                    <span>Last check date: {formatDate(card.lastCheckTime)}</span>
                                    <span>
                                        Next check time: {formatDateTime(card.nextCheckTime)}
                                        <ResetButton onClick={resetNextCheckHandler}>Reset</ResetButton>
                                    </span>
                                    {tags.length > 0 && (
                                        <TagsContainer>
                                            {tags.map((tag, index) => (
                                                <Tag
                                                    key={index}
                                                    onClick={() => handleTagClick(tag)}
                                                >
                                                    {tag}
                                                </Tag>
                                            ))}
                                        </TagsContainer>
                                    )}
                                </MetaBody>
                            )}
                        </MetaPanel>
                        <Card $isSmall={isSmallCard}>
                            {card.mark?.wholeCard === true && (
                                <GenerationNotice data-testid="card-mark-notice">
                                    <span>
                                        🚩 Card marked for regeneration{card.mark.note !== '' ? ': ' : ''}
                                        {card.mark.note !== '' && <MarkNote>{card.mark.note}</MarkNote>}
                                    </span>
                                    <ResetButton onClick={unmarkWholeCardHandler}>Remove</ResetButton>
                                </GenerationNotice>
                            )}
                            {card.mark !== undefined && card.mark.subcards.length > 0 && (
                                <GenerationNotice data-testid="subcard-marks-notice">
                                    <span>🚩 Marked subcards:</span>
                                    <SubcardMarkList>
                                        {card.mark.subcards.map(subcard => (
                                            <SubcardMarkItem key={subcard.subcardKey} data-testid="subcard-mark">
                                                <span>
                                                    «{subcardQuestionLine(subcard.question)}»
                                                    {subcard.note !== '' && <> — <MarkNote>{subcard.note}</MarkNote></>}
                                                </span>
                                                <ResetButton
                                                    title="Remove this subcard's mark"
                                                    onClick={() => unmarkSubcardHandler(subcard.subcardKey)}
                                                >
                                                    ✕
                                                </ResetButton>
                                            </SubcardMarkItem>
                                        ))}
                                    </SubcardMarkList>
                                </GenerationNotice>
                            )}
                            {card.generation?.status === 'running' && (
                                <GenerationNotice data-testid="card-generation-running">
                                    {GENERATION_LABELS[card.generation.kind].running}…
                                </GenerationNotice>
                            )}
                            {card.generation?.status === 'error' && (
                                <GenerationNotice data-testid="card-generation-error">
                                    {GENERATION_LABELS[card.generation.kind].failed}: {card.generation.error ?? 'unknown error'}
                                    <ResetButton onClick={dismissGenerationHandler}>Dismiss</ResetButton>
                                </GenerationNotice>
                            )}
                            <CardMarkupViewer
                                cardId={props.cardId}
                                markup={card.markup}
                                state={CardMarkupViewerState.Full}
                                showSeparators={true}
                                subcardStats={subcardStats}
                            />
                        </Card>
                    </CardContainer>
                    <FloatingButton
                        icon={FloatingButtonIcon.Edit}
                        onClick={editHandler}
                    />
                </>
            )}
        </Root>
    );
};
CardViewComponent.displayName = 'CardViewComponent';

export const CardView = memo(CardViewComponent);
CardView.displayName = 'CardView';